/**
 * crmFunnel/service.ts
 *
 * Public API for the CRM Funnel module.
 *
 * getCrmFunnelData(rows, filters) → CrmFunnelComparison
 *
 * ARCHITECTURE CONTRACT:
 *   - No UI imports. Pure data logic only.
 *   - All calculations use aggregateTotals → deriveMetrics pattern.
 *   - Previous period uses the same non-date filters as current period.
 *   - Switch from mock data to real Close CRM API by replacing the rows
 *     argument with adaptCloseCrmResponse() output — no changes here.
 */

import type { CrmLeadRow, CrmFunnelFilters, CrmFunnelComparison, CrmFunnelFilterOptions } from "./types";
import { applyFilters, getPreviousPeriodRows, buildFilterOptions } from "./filters";
import { aggregateTotals, deriveMetrics, buildChanges } from "./calculations";

// ─────────────────────────────────────────────
// PRIMARY SERVICE FUNCTION
// ─────────────────────────────────────────────

/**
 * Computes current-period and previous-period CRM funnel metrics,
 * along with percentage changes for each KPI.
 *
 * @param rows    Full raw lead row array (e.g. RAW_CRM_FUNNEL_ROWS)
 * @param filters Active filter state including a required dateRange
 * @returns       CrmFunnelComparison { current, previous, changes }
 */
export function getCrmFunnelData(
  rows: CrmLeadRow[],
  filters: CrmFunnelFilters
): CrmFunnelComparison {
  // ── Current period ────────────────────────────────────────────────────────
  const currentRows    = applyFilters(rows, filters);
  const currentTotals  = aggregateTotals(currentRows);
  const current        = deriveMetrics(currentTotals);

  // ── Previous period ───────────────────────────────────────────────────────
  // 1. Pull rows that fall in the previous date range
  const prevDateRows = getPreviousPeriodRows(rows, filters.dateRange);
  // 2. Apply all non-date filters (campaign, adset, ad, source, platform)
  const { dateRange: _ignored, ...nonDateFilters } = filters;
  const prevFakeRange = { start: "0000-01-01", end: "9999-12-31" }; // pass-through
  const previousRows  = applyFilters(prevDateRows, { dateRange: prevFakeRange, ...nonDateFilters });
  const previousTotals = aggregateTotals(previousRows);
  const previous       = deriveMetrics(previousTotals);

  // ── Changes ───────────────────────────────────────────────────────────────
  const changes = buildChanges(current, previous);

  return { current, previous, changes };
}

// ─────────────────────────────────────────────
// FILTER OPTIONS
// ─────────────────────────────────────────────

/**
 * Returns all available filter option values derived from the full dataset.
 * Call once and memoize — options don't change with filter state.
 */
export function getCrmFunnelFilterOptions(rows: CrmLeadRow[]): CrmFunnelFilterOptions {
  return buildFilterOptions(rows);
}
